import { useState } from "react";
import Button from "../../components/common/Button";
import {
  faPlus,
  faArrowRight,
  faUser,
  faCalendarAlt,
  faLayerGroup,
  faTrophy,
  faPalette,
  faFont,
  faPuzzlePiece,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PageHeader from "../../components/common/PageHeader";
import Loading from "../../components/common/Loading";
import UpcomingEventsCard from "../../components/common/UpcomingEventsCard";
import LineupSlot from "../../components/lineup/LineupSlot";
import AlternatePickSlot from "../../components/lineup/AlternatePickSlot";
import FighterSelectionModal from "../../components/lineup/FighterSelectionModal";
import InfoTooltip from "../../components/common/InfoTooltip";
// import ScoringSummaryExample from "../../components/scoring/ScoringSummaryExample";

const StyleGuide = () => {
  const [activeSection, setActiveSection] = useState("colors");
  const [showFighterSelectionModal, setShowFighterSelectionModal] =
    useState(false);

  // Navigation items for the sidebar
  const navItems = [
    { id: "colors", label: "Colors", icon: faPalette },
    { id: "typography", label: "Typography", icon: faFont },
    { id: "components", label: "Components", icon: faPuzzlePiece },
  ];

  // Component navigation items
  const componentNavItems = [
    { id: "pageheader", label: "PageHeader" },
    { id: "button", label: "Button" },
    { id: "loading", label: "Loading" },
    { id: "upcoming-events", label: "UpcomingEventsCard" },
    { id: "lineup-components", label: "Lineup Components" },
    { id: "tooltip", label: "InfoTooltip" },
    { id: "scoring-summary", label: "Scoring Summary" },
  ];

  // Sample fighter data for demos
  const sampleFighter = {
    FighterId: "1",
    FirstName: "Jon",
    LastName: "Jones",
    WeightClass: "Heavyweight",
    Moneyline: -250,
  };

  const sampleOpponent = {
    FighterId: "2",
    FirstName: "Stipe",
    LastName: "Miocic",
    WeightClass: "Heavyweight",
    Moneyline: +200,
  };

  const sampleAlternateFighter = {
    FighterId: "3",
    FirstName: "Alex",
    LastName: "Pereira",
    WeightClass: "Light Heavyweight",
    Moneyline: -180,
  };

  const sampleAlternateOpponent = {
    FighterId: "4",
    FirstName: "Jamahal",
    LastName: "Hill",
    WeightClass: "Light Heavyweight",
    Moneyline: +150,
  };

  // Mock fighters array for fighter selection modal
  const mockFighters = [
    sampleFighter,
    sampleOpponent,
    sampleAlternateFighter,
    sampleAlternateOpponent,
    {
      FighterId: "5",
      FirstName: "Sean",
      LastName: "O'Malley",
      WeightClass: "Bantamweight",
      Moneyline: -200,
    },
    {
      FighterId: "6",
      FirstName: "Marlon",
      LastName: "Vera",
      WeightClass: "Bantamweight",
      Moneyline: +170,
    },
  ];

  // Scroll to section helper
  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div>
      <PageHeader
        title="Style Guide"
        subtitle="Components, colors, and typography guidelines"
        icon={faLayerGroup}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="md:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md p-4 sticky top-4">
              <h2 className="text-lg font-bold text-dark-grey mb-4 border-b pb-2">
                Navigation
              </h2>
              <ul className="space-y-1">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full text-left px-3 py-2 rounded flex items-center ${
                        activeSection === item.id ||
                        (activeSection.includes("-") &&
                          item.id === "components")
                          ? "bg-deep-red text-white font-medium"
                          : "text-medium-grey hover:bg-light-grey"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="mr-2 w-4 h-4"
                      />
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Component sub-navigation */}
              {(activeSection === "components" ||
                activeSection.includes("-")) && (
                <div className="mt-4 pl-4">
                  <h3 className="text-sm font-medium text-medium-grey mb-2">
                    Components
                  </h3>
                  <ul className="space-y-1">
                    {componentNavItems.map((item) => (
                      <li key={item.id}>
                        <button
                          onClick={() =>
                            scrollToSection(`component-${item.id}`)
                          }
                          className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center ${
                            activeSection === `component-${item.id}`
                              ? "bg-light-grey font-medium text-dark-grey"
                              : "text-medium-grey hover:bg-light-grey/50"
                          }`}
                        >
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Colors Section */}
            <section id="colors" className="mb-12 scroll-mt-16">
              <div className="flex items-center mb-4">
                <FontAwesomeIcon
                  icon={faPalette}
                  className="text-deep-red mr-3 text-xl"
                />
                <h2 className="text-2xl font-bold text-dark-grey border-b pb-2 flex-1">
                  Colors
                </h2>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <p className="text-medium-grey mb-6">
                  Our brand colors represent strength, energy, and authority.
                  Consistent use of these colors helps maintain brand
                  recognition.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <div className="h-24 bg-deep-red rounded-lg shadow-md"></div>
                    <p className="mt-3 text-sm font-medium text-dark-grey">
                      Deep Red
                    </p>
                    <p className="text-xs font-mono text-medium-grey">
                      #D02028
                    </p>
                    <p className="text-xs font-mono text-medium-grey mt-1">
                      bg-deep-red
                    </p>
                  </div>
                  <div>
                    <div className="h-24 bg-dark-grey rounded-lg shadow-md"></div>
                    <p className="mt-3 text-sm font-medium text-dark-grey">
                      Dark Grey
                    </p>
                    <p className="text-xs font-mono text-medium-grey">
                      #222222
                    </p>
                    <p className="text-xs font-mono text-medium-grey mt-1">
                      bg-dark-grey
                    </p>
                  </div>
                  <div>
                    <div className="h-24 bg-medium-grey rounded-lg shadow-md"></div>
                    <p className="mt-3 text-sm font-medium text-dark-grey">
                      Medium Grey
                    </p>
                    <p className="text-xs font-mono text-medium-grey">
                      #666666
                    </p>
                    <p className="text-xs font-mono text-medium-grey mt-1">
                      bg-medium-grey
                    </p>
                  </div>
                  <div>
                    <div className="h-24 bg-light-grey rounded-lg shadow-md"></div>
                    <p className="mt-3 text-sm font-medium text-dark-grey">
                      Light Grey
                    </p>
                    <p className="text-xs font-mono text-medium-grey">
                      #E5E5E5
                    </p>
                    <p className="text-xs font-mono text-medium-grey mt-1">
                      bg-light-grey
                    </p>
                  </div>
                  <div>
                    <div className="h-24 bg-gold rounded-lg shadow-md"></div>
                    <p className="mt-3 text-sm font-medium text-dark-grey">
                      Gold
                    </p>
                    <p className="text-xs font-mono text-medium-grey">
                      #F0B042
                    </p>
                    <p className="text-xs font-mono text-medium-grey mt-1">
                      bg-gold
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Typography Section */}
            <section id="typography" className="mb-12 scroll-mt-16">
              <div className="flex items-center mb-4">
                <FontAwesomeIcon
                  icon={faFont}
                  className="text-deep-red mr-3 text-xl"
                />
                <h2 className="text-2xl font-bold text-dark-grey border-b pb-2 flex-1">
                  Typography
                </h2>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <p className="text-medium-grey mb-6">
                  Our typography system is designed for readability and
                  consistent hierarchy across all screens.
                </p>
                <div className="space-y-8">
                  <div>
                    <h1 className="text-3xl font-bold text-dark-grey">
                      Heading 1
                    </h1>
                    <p className="text-sm text-medium-grey mt-1">
                      Used for main page titles and major sections
                    </p>
                    <p className="text-xs text-medium-grey font-mono mt-1">
                      text-3xl font-bold text-dark-grey
                    </p>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-dark-grey">
                      Heading 2
                    </h2>
                    <p className="text-sm text-medium-grey mt-1">
                      Used for section titles and card headers
                    </p>
                    <p className="text-xs text-medium-grey font-mono mt-1">
                      text-2xl font-bold text-dark-grey
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-dark-grey">
                      Heading 3
                    </h3>
                    <p className="text-sm text-medium-grey mt-1">
                      Used for subsections and important callouts
                    </p>
                    <p className="text-xs text-medium-grey font-mono mt-1">
                      text-xl font-bold text-dark-grey
                    </p>
                  </div>
                  <div>
                    <p className="text-base text-medium-grey">Body Text</p>
                    <p className="text-sm text-medium-grey mt-1">
                      Used for the main content and descriptions
                    </p>
                    <p className="text-xs text-medium-grey font-mono mt-1">
                      text-base text-medium-grey
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-medium-grey">Small Text</p>
                    <p className="text-sm text-medium-grey mt-1">
                      Used for supplementary information and metadata
                    </p>
                    <p className="text-xs text-medium-grey font-mono mt-1">
                      text-sm text-medium-grey
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Components Section */}
            <section id="components" className="mb-12 scroll-mt-16">
              <div className="flex items-center mb-4">
                <FontAwesomeIcon
                  icon={faPuzzlePiece}
                  className="text-deep-red mr-3 text-xl"
                />
                <h2 className="text-2xl font-bold text-dark-grey border-b pb-2 flex-1">
                  Components
                </h2>
              </div>
              <p className="text-medium-grey mb-6">
                Our component library provides consistent UI elements across the
                application.
              </p>

              {/* PageHeader Component */}
              <div id="component-pageheader" className="mb-12 scroll-mt-16">
                <h3 className="text-xl font-bold mb-4 text-dark-grey border-l-4 border-deep-red pl-3 py-1">
                  PageHeader
                </h3>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <p className="text-medium-grey mb-6">
                    A consistent header component used at the top of each page,
                    with title, subtitle, and icon.
                  </p>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                      Example Usage
                    </h4>
                    <div className="bg-light-grey/30 p-4 rounded">
                      <code className="text-sm font-mono">
                        {`<PageHeader 
  title="Page Title" 
  subtitle="Description of this page's purpose"
  icon={faIcon}
  backgroundIcon={faBackgroundIcon} // Optional
  showEmblem={true} // Optional
/>`}
                      </code>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                      Live Example
                    </h4>
                    <div className="border border-light-grey rounded-lg overflow-hidden">
                      <PageHeader
                        title="Example Header"
                        subtitle="This is how the header appears on pages"
                        icon={faCalendarAlt}
                        backgroundIcon={faTrophy}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Button Component */}
              <div id="component-button" className="mb-12 scroll-mt-16">
                <h3 className="text-xl font-bold mb-4 text-dark-grey border-l-4 border-deep-red pl-3 py-1">
                  Button
                </h3>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <p className="text-medium-grey mb-6">
                    Buttons allow users to take actions and make choices with a
                    single tap.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                        Primary Buttons
                      </h4>
                      <div className="bg-light-grey/30 p-4 rounded">
                        <div className="flex flex-wrap gap-3 items-center mb-4">
                          <Button variant="primary" size="xs">
                            Extra Small
                          </Button>
                          <Button variant="primary" size="sm">
                            Small
                          </Button>
                          <Button variant="primary" size="md">
                            Medium
                          </Button>
                          <Button variant="primary" size="lg">
                            Large
                          </Button>
                        </div>
                        <div className="mt-4">
                          <Button
                            variant="primary"
                            size="md"
                            icon={faPlus}
                            className="mr-3"
                          >
                            With Icon
                          </Button>
                          <Button
                            variant="primary"
                            size="md"
                            icon={faArrowRight}
                            iconPosition="right"
                          >
                            Icon Right
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                        Secondary Buttons
                      </h4>
                      <div className="bg-light-grey/30 p-4 rounded">
                        <div className="flex flex-wrap gap-3 items-center mb-4">
                          <Button variant="secondary" size="xs">
                            Extra Small
                          </Button>
                          <Button variant="secondary" size="sm">
                            Small
                          </Button>
                          <Button variant="secondary" size="md">
                            Medium
                          </Button>
                          <Button variant="secondary" size="lg">
                            Large
                          </Button>
                        </div>
                        <div className="mt-4">
                          <Button
                            variant="secondary"
                            size="md"
                            icon={faPlus}
                            className="mr-3"
                          >
                            With Icon
                          </Button>
                          <Button
                            variant="secondary"
                            size="md"
                            icon={faArrowRight}
                            iconPosition="right"
                          >
                            Icon Right
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                        Outline Buttons
                      </h4>
                      <div className="bg-light-grey/30 p-4 rounded">
                        <div className="flex flex-wrap gap-3 items-center mb-4">
                          <Button variant="outline" size="xs">
                            Extra Small
                          </Button>
                          <Button variant="outline" size="sm">
                            Small
                          </Button>
                          <Button variant="outline" size="md">
                            Medium
                          </Button>
                          <Button variant="outline" size="lg">
                            Large
                          </Button>
                        </div>
                        <div className="mt-4">
                          <Button
                            variant="outline"
                            size="md"
                            icon={faUser}
                            className="mr-3"
                          >
                            With Icon
                          </Button>
                          <Button variant="outline" size="md" disabled>
                            Disabled
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                        Usage Example
                      </h4>
                      <div className="bg-light-grey/30 p-4 rounded">
                        <code className="text-xs font-mono block">
                          {`<Button
  variant="primary" // primary, secondary, outline, link, custom
  size="md" // xs, sm, md, lg
  icon={faIcon} // Optional FontAwesome icon
  iconPosition="right" // left (default) or right
  onClick={handleAction}
  className="custom-class" // Optional additional classes
  fullWidth={false} // Optional - makes button full width
  disabled={false} // Optional - disables the button
>
  Button Text
</Button>`}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Loading Component */}
              <div id="component-loading" className="mb-12 scroll-mt-16">
                <h3 className="text-xl font-bold mb-4 text-dark-grey border-l-4 border-deep-red pl-3 py-1">
                  Loading
                </h3>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <p className="text-medium-grey mb-6">
                    The Loading component provides visual feedback when content
                    is being loaded.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                        Full-Screen Loading
                      </h4>
                      <div className="bg-light-grey/30 p-4 rounded h-64 flex items-center justify-center">
                        <Loading fullScreen={false} />
                      </div>
                      <p className="text-xs text-medium-grey mt-2 italic">
                        Note: Actual component takes up full viewport height
                      </p>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                        Usage Example
                      </h4>
                      <div className="bg-light-grey/30 p-4 rounded">
                        <code className="text-sm font-mono block mb-4">
                          {`import Loading from '../../components/common/Loading';

// Full-screen loading (default):
{isLoading && <Loading />}

// For inline loading inside a component:
{isLoadingSection && <Loading fullScreen={false} />}

// Conditional rendering:
return isLoading ? <Loading /> : <YourPageContent />;`}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* UpcomingEventsCard Component */}
              <div
                id="component-upcoming-events"
                className="mb-12 scroll-mt-16"
              >
                <h3 className="text-xl font-bold mb-4 text-dark-grey border-l-4 border-deep-red pl-3 py-1">
                  UpcomingEventsCard
                </h3>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <p className="text-medium-grey mb-6">
                    Card component displaying upcoming events with league
                    information, optimized for different layouts.
                  </p>

                  <div className="space-y-8 mb-8">
                    {/* Single Column View (1/3 of total layout width) */}
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                        Single Column View (1/3 width)
                      </h4>
                      <p className="text-sm text-medium-grey mb-3">
                        Used in GlobalCompetition page (lg:col-span-4)
                      </p>
                      <div className="bg-light-grey/30 p-4 rounded">
                        <div className="max-w-sm mx-auto">
                          <UpcomingEventsCard
                            events={[
                              {
                                id: "ufc300",
                                name: "UFC 300: Pereira vs. Hill",
                                monthShort: "Apr",
                                day: "13",
                                formattedTime: "10:00 PM",
                                showBadge: true,
                                badgeText: "Today",
                                leagues: [
                                  { id: "league1", name: "Pro League" },
                                  { id: "league2", name: "Fantasy MMA" },
                                ],
                              },
                              {
                                id: "ufc301",
                                name: "UFC 301: Pantoja vs. Erceg",
                                monthShort: "May",
                                day: "4",
                                formattedTime: "10:00 PM",
                                showBadge: false,
                                leagues: [
                                  { id: "league1", name: "Pro League" },
                                  { id: "league3", name: "MMA Elite" },
                                ],
                              },
                            ]}
                            isLoading={false}
                            onEventClick={(id, name) =>
                              console.log(`Event clicked: ${id} - ${name}`)
                            }
                            onViewAllClick={() =>
                              console.log("View all clicked")
                            }
                            onLeagueClick={(leagueId, eventId) =>
                              console.log(
                                `League clicked: ${leagueId} for event ${eventId}`
                              )
                            }
                            title="Upcoming Events"
                            subtitle="Next UFC & MMA events"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Double Column View (2/3 of total layout width) */}
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                        Double Column View (2/3 width)
                      </h4>
                      <p className="text-sm text-medium-grey mb-3">
                        Used in Home page (lg:col-span-8)
                      </p>
                      <div className="bg-light-grey/30 p-4 rounded">
                        <div className="max-w-2xl mx-auto">
                          <UpcomingEventsCard
                            events={[
                              {
                                id: "ufc300",
                                name: "UFC 300: Pereira vs. Hill",
                                monthShort: "Apr",
                                day: "13",
                                formattedTime: "10:00 PM",
                                showBadge: true,
                                badgeText: "Today",
                                leagues: [
                                  { id: "league1", name: "Pro League" },
                                  { id: "league2", name: "Fantasy MMA" },
                                ],
                              },
                              {
                                id: "ufc301",
                                name: "UFC 301: Pantoja vs. Erceg",
                                monthShort: "May",
                                day: "4",
                                formattedTime: "10:00 PM",
                                showBadge: false,
                                leagues: [
                                  { id: "league1", name: "Pro League" },
                                  { id: "league3", name: "MMA Elite" },
                                ],
                              },
                            ]}
                            isLoading={false}
                            onEventClick={(id, name) =>
                              console.log(`Event clicked: ${id} - ${name}`)
                            }
                            onViewAllClick={() =>
                              console.log("View all clicked")
                            }
                            onLeagueClick={(leagueId, eventId) =>
                              console.log(
                                `League clicked: ${leagueId} for event ${eventId}`
                              )
                            }
                            title="Upcoming Events"
                            subtitle="Next UFC & MMA events"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                      Usage Example
                    </h4>
                    <div className="bg-light-grey/30 p-4 rounded">
                      <code className="text-xs font-mono block mb-2">
                        {`// Format events for the display component
const formattedEvents = events.map(event => ({
  id: event.EventId,
  name: event.Name,
  monthShort: new Date(event.DateTime).toLocaleString('en-US', { month: 'short' }),
  day: new Date(event.DateTime).getDate(),
  formattedTime: new Date(event.DateTime).toLocaleTimeString(...),
  showBadge: isEventSoon(event.DateTime),
  badgeText: isEventToday(event.DateTime) ? 'Today' : 'Tomorrow',
  leagues: event.userLeagues || []
}));

<UpcomingEventsCard 
  events={formattedEvents}
  isLoading={isLoading}
  onEventClick={(eventId, eventName) => navigate(\`/events/\${eventId}\`)}
  onViewAllClick={() => navigate('/events')}
  onLeagueClick={(leagueId, eventId) => {
    dispatch(setLeagueId(leagueId));
    navigate(\`/events/\${eventId}\`);
  }}
  title="Upcoming Events"
  subtitle="Next UFC & MMA events"
/>`}
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Lineup Components Section */}
            <div
              id="component-lineup-components"
              className="mb-12 scroll-mt-16"
            >
              <h3 className="text-xl font-bold mb-4 text-dark-grey border-l-4 border-deep-red pl-3 py-1">
                Lineup Components
              </h3>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <p className="text-medium-grey mb-6">
                  Components used for the lineup creation and submission flow.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                      Empty Lineup Slot
                    </h4>
                    <div className="bg-light-grey/30 p-4 rounded">
                      <LineupSlot
                        position={0}
                        fighter={null}
                        onClick={() => alert("Slot clicked")}
                        slotInfo="8× multiplier"
                      />
                    </div>
                    <div className="text-xs font-mono text-medium-grey mt-2 bg-light-grey/30 p-3 rounded">
                      {`<LineupSlot
  position={0}
  fighter={null}
  onClick={handleSlotClick}
  slotInfo="8× multiplier"
/>`}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                      Filled Lineup Slot
                    </h4>
                    <div className="bg-light-grey/30 p-4 rounded">
                      <LineupSlot
                        position={0}
                        fighter={sampleFighter}
                        opponent={sampleOpponent}
                        onClick={() => alert("Slot clicked")}
                        onToggleLotn={() => alert("Toggle LOTN")}
                        onRemove={() => alert("Remove fighter")}
                        isLotn={true}
                        slotInfo="8× multiplier"
                      />
                    </div>
                    <div className="text-xs font-mono text-medium-grey mt-2 bg-light-grey/30 p-3 rounded">
                      {`<LineupSlot
  position={0}
  fighter={fighter}
  opponent={opponent}
  onClick={handleSlotClick}
  onToggleLotn={handleToggleLotn}
  onRemove={handleRemoveFighter}
  isLotn={true}
  slotInfo="8× multiplier"
/>`}
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                    Alternate Pick Slot
                  </h4>
                  <div className="max-w-xs bg-light-grey/30 p-4 rounded">
                    <AlternatePickSlot
                      fighter={sampleAlternateFighter}
                      opponent={sampleAlternateOpponent}
                      onRemove={() => alert("Remove alternate")}
                      onToggleAltLotn={() => alert("Toggle Alt LOTN")}
                      isAltLotn={true}
                    />
                  </div>
                  <div className="text-xs font-mono text-medium-grey mt-2 bg-light-grey/30 p-3 rounded">
                    {`<AlternatePickSlot
  fighter={fighter}
  opponent={opponent}
  onRemove={handleRemoveAlternate}
  onToggleAltLotn={handleToggleAlternateLOTN}
  isAltLotn={true}
/>`}
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                    Fighter Selection Modal
                  </h4>
                  <p className="text-medium-grey mb-4">
                    Modal for selecting fighters, grouped by weight class.
                  </p>

                  <button
                    onClick={() => setShowFighterSelectionModal(true)}
                    className="bg-deep-red text-white py-2 px-4 rounded-lg hover:bg-deep-red/90 transition-colors"
                  >
                    Show Fighter Selection Modal
                  </button>

                  {showFighterSelectionModal && (
                    <FighterSelectionModal
                      position={0}
                      onClose={() => setShowFighterSelectionModal(false)}
                      onSelect={() => {
                        alert("Fighter selected");
                        setShowFighterSelectionModal(false);
                      }}
                      allFighters={mockFighters}
                      lineup={Array(5).fill(null)}
                      isFighterOrOpponentInLineup={() => false}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* InfoTooltip Component */}
            <div id="component-tooltip" className="mb-12 scroll-mt-16">
              <h3 className="text-xl font-bold mb-4 text-dark-grey border-l-4 border-deep-red pl-3 py-1">
                InfoTooltip
              </h3>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <p className="text-medium-grey mb-6">
                  A tooltip component to display helpful information on hover or
                  click.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                      Bottom Position (Default)
                    </h4>
                    <div className="bg-light-grey/30 p-4 rounded flex justify-center">
                      <InfoTooltip>
                        <p className="text-dark-grey font-medium mb-1">
                          Information Title
                        </p>
                        <p className="text-sm text-medium-grey">
                          This is a tooltip with helpful information for users.
                        </p>
                      </InfoTooltip>
                    </div>
                    <div className="text-xs font-mono text-medium-grey mt-2 bg-light-grey/30 p-3 rounded">
                      {`<InfoTooltip>
  <p className="text-dark-grey font-medium mb-1">Information Title</p>
  <p className="text-sm text-medium-grey">Tooltip content goes here</p>
</InfoTooltip>`}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-dark-grey">
                      Different Positions
                    </h4>
                    <div className="bg-light-grey/30 p-4 rounded flex justify-around items-center">
                      <InfoTooltip position="top">
                        <p className="font-medium">Top Position</p>
                      </InfoTooltip>

                      <InfoTooltip position="left">
                        <p className="font-medium">Left Position</p>
                      </InfoTooltip>

                      <InfoTooltip position="right">
                        <p className="font-medium">Right Position</p>
                      </InfoTooltip>
                    </div>
                    <div className="text-xs font-mono text-medium-grey mt-2 bg-light-grey/30 p-3 rounded">
                      {`<InfoTooltip position="top|left|right|bottom">
  <p className="font-medium">Tooltip content</p>
</InfoTooltip>`}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Scoring Summary Component */}
            <div id="component-scoring-summary" className="mb-12 scroll-mt-16">
              <h3 className="text-xl font-bold mb-4 text-dark-grey border-l-4 border-deep-red pl-3 py-1">
                Scoring Summary
              </h3>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <p className="text-medium-grey mb-6">
                  Live scoring summary display showing how fighters earn points
                  during events. Format: Base total win (+X.XX) + LOTN (+X.XX) +
                  round Y of Z (+N)
                </p>
                {/* <ScoringSummaryExample /> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StyleGuide;
